import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { useWindowDimensions, type View } from 'react-native'

import { useScrollListenerContext } from '../components/ScrollListenerProvider'
import { useIsMounted } from './useIsMounted'
import { useOnChange } from './useOnChange'
import { useSyncedRef } from './useSyncedRef'

// Constants
const DEFAULT_PERCENT_WIDTH = 100 // 100%
const DEFAULT_PERCENT_HEIGHT = 100 // 100%
const LAYOUT_CHECK_DELAY = 100

type TLayoutRectangle = {
  x: number
  y: number
  width: number
  height: number
}

type TCheckIntersectionParams = {
  /**
   * The minimum percentage of width required for the child to be considered in the viewport.
   * Must be between 0 and 100.
   * @default 100
   */
  percentWidth?: number

  /**
   * The minimum percentage of height required for the child to be considered in the viewport.
   * Must be between 0 and 100.
   * @default 100
   */
  percentHeight?: number
}

/** Function to check if the child view is in the viewport */
const checkIntersection = (
  view1: TLayoutRectangle,
  view2: TLayoutRectangle,
  { percentWidth = DEFAULT_PERCENT_WIDTH, percentHeight = DEFAULT_PERCENT_HEIGHT }: TCheckIntersectionParams,
) => {
  // Calculate the intersection rectangle
  const intersectionLeft = Math.max(view2.x, view1.x)
  const intersectionRight = Math.min(view2.x + view2.width, view1.x + view1.width)
  const intersectionTop = Math.max(view2.y, view1.y)
  const intersectionBottom = Math.min(view2.y + view2.height, view1.y + view1.height)

  // If there's no intersection, return false
  if (intersectionRight <= intersectionLeft || intersectionBottom <= intersectionTop) {
    return false
  }

  // Calculate intersection area
  const intersectionWidth = intersectionRight - intersectionLeft
  const intersectionHeight = intersectionBottom - intersectionTop

  // Calculate the percentage of the child that is visible
  const visibleWidthPercentage = intersectionWidth / view2.width
  const visibleHeightPercentage = intersectionHeight / view2.height

  // Check if the visible percentage meets the threshold
  return visibleWidthPercentage >= percentWidth / 100 && visibleHeightPercentage >= percentHeight / 100
}

type TVisibilityProps = {
  onVisibilityChange: (isVisible: boolean) => void
} & TCheckIntersectionParams

export const useVisibility = ({ onVisibilityChange, percentWidth, percentHeight }: TVisibilityProps) => {
  // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- ref bridge
  const viewRef = useRef<View>(null) as RefObject<View>
  const [isVisible, setIsVisible] = useState(false)
  const [checkVisibilityTrigger, setCheckVisibilityTrigger] = useState(0)
  const isMounted = useIsMounted()
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const { addScrollListener } = useScrollListenerContext()

  useOnChange(percentWidth, () => {
    if (percentWidth !== undefined && (percentWidth < 0 || percentWidth > 100)) {
      console.warn('useVisibility: percentWidth must be between 0 and 100')
    }
  })
  useOnChange(percentHeight, () => {
    if (percentHeight !== undefined && (percentHeight < 0 || percentHeight > 100)) {
      console.warn('useVisibility: percentHeight must be between 0 and 100')
    }
  })

  const checkVisibility = useCallback(() => {
    if (!viewRef.current || !screenWidth || !screenHeight || !isMounted()) {
      return
    }

    try {
      // oxlint-disable-next-line max-params
      viewRef.current.measure((_x, _y, width, height, pageX, pageY) => {
        // Skip if we don't have valid measurements
        if (width === undefined || height === undefined || pageX === undefined || pageY === undefined) {
          console.warn('useVisibility: Invalid measurements received')
          return
        }

        const newIsVisible = checkIntersection(
          { x: 0, y: 0, width: screenWidth, height: screenHeight },
          { x: pageX, y: pageY, width, height },
          { percentWidth, percentHeight },
        )

        if (isMounted()) setIsVisible(newIsVisible)
      })
    } catch (error: unknown) {
      console.error('useVisibility: Error measuring view:', error instanceof Error ? error.message : String(error))
    }
  }, [screenWidth, screenHeight, isMounted, percentWidth, percentHeight, viewRef])

  // Trigger checkVisibility on scroll
  useEffect(() => addScrollListener(checkVisibility), [addScrollListener, checkVisibility])

  // Trigger checkVisibility on layout
  const onLayout = useCallback(() => {
    // Increment checkVisibilityTrigger to trigger checkVisibility timeout,
    // so that the layout has time to complete before the visibility check
    setCheckVisibilityTrigger((prevSetTimeoutCount) => prevSetTimeoutCount + 1)
  }, [])

  // Check visibility on checkVisibilityTrigger change
  useEffect(() => {
    // Prevent checkVisibility from being called on first render so that the first layout has time to complete
    if (checkVisibilityTrigger > 0) {
      const timeout = setTimeout(checkVisibility, LAYOUT_CHECK_DELAY)
      return () => clearTimeout(timeout)
    }
  }, [checkVisibility, checkVisibilityTrigger])

  const onVisibilityChangeRef = useSyncedRef(onVisibilityChange)
  useEffect(() => {
    onVisibilityChangeRef.current(isVisible)
  }, [isVisible, onVisibilityChangeRef])

  return { ref: viewRef, onLayout }
}
